import { TabKey } from '@/pages/Circuit/LabelsManage';
import {
  AlarmStatus,
  alarmStatusDisplay,
  alarmStatusOptions,
} from '@/pages/Device/components/SelectRuleGroupModal/types';
import {
  CustomFilter,
  EllipsisSpan,
  Form,
  Input,
  Key,
  MultiSelect,
  Paging,
  Select,
  Table,
  usePaging,
} from '@maxtropy/components';
import {
  apiV2ComprehensiveLabelListPost,
  apiV2RuleGroupDeviceAllRuleGroupPost,
  apiV2RuleRulePagePost,
  V2RuleRulePagePostRequest,
  V2RuleRulePagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import React, { Ref, useImperativeHandle, useMemo, useState } from 'react';

interface RuleModalProps {
  ruleIds?: number[];
}

export interface RuleModalRef {
  selectedRowKeys?: number[];
}

type SearchParams = Omit<V2RuleRulePagePostRequest, 'page' | 'size'>;

export type RuleItem = Exclude<V2RuleRulePagePostResponse['list'], undefined>[number];

const RuleModal = React.forwardRef((props: RuleModalProps, ref: Ref<RuleModalRef>) => {
  const { ruleIds } = props;
  const [form] = Form.useForm();
  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number> | undefined>(ruleIds || undefined);
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const { data: labelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 2 }));

  const { data, loading: isLoading } = useRequest(
    () =>
      apiV2RuleRulePagePost({
        page: pageOffset,
        size: pageSize,
        ...searchParams,
      }).then(res => {
        setTotalCount(res.total!);
        return res.list;
      }),
    {
      refreshDeps: [pageOffset, pageSize, searchParams],
    }
  );

  // 规则组名称
  const { data: nameList } = useRequest(() =>
    apiV2RuleGroupDeviceAllRuleGroupPost({}).then(res => {
      return res.list;
    })
  );

  // options
  const nameListOptions = useMemo(() => {
    return (nameList ?? []).map(item => ({
      label: item.name,
      value: item.id,
    }));
  }, [nameList]);

  const columns = [
    {
      title: '规则名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '规则状态',
      dataIndex: 'status',
      ellipsis: { showTitle: true },
      render: (v: AlarmStatus) => <EllipsisSpan value={alarmStatusDisplay[v]} />,
    },
    {
      title: '规则编号',
      dataIndex: 'code',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属规则组',
      dataIndex: 'ruleGroupName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '报警标签',
      dataIndex: 'labelIds',
      ellipsis: { showTitle: true },
      render: (v: any) => {
        const idToName = new Map((labelList?.list ?? []).map(l => [l.id, l.name]));
        const names = (Array.isArray(v) ? v : []).map(id => idToName.get(id) ?? String(id));
        return <EllipsisSpan value={names.join(',')} />;
      },
    },
  ];
  const onFinish = (value: SearchParams) => {
    setSearchParams(value);
    setPageOffset(1);
  };
  const onReset = () => {
    setSearchParams({});
    form.resetFields();
    setPageOffset(1);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (rowKeys: Key[]) => {
      setSelectedRowKeys(rowKeys as number[]);
    },
    selectedRowKeys: selectedRowKeys,
  };

  useImperativeHandle(ref, () => ({ selectedRowKeys }));

  const filters = (
    <CustomFilter form={form} onFinish={val => onFinish(val as SearchParams)} colSpan={8} onReset={onReset}>
      <Form.Item name="codeOrNameLike" label="编号/名称">
        <Input placeholder="请输入编号或名称查询" />
      </Form.Item>

      <Form.Item label="规则组名称" name="ruleGroupIdList">
        <MultiSelect placeholder="请选择" showSearch allowClear options={nameListOptions} />
      </Form.Item>

      <Form.Item label="报警状态" name="status">
        <Select placeholder="请选择" allowClear options={alarmStatusOptions} />
      </Form.Item>
      <Form.Item label="报警标签" name="labelIds">
        <Select
          placeholder="请选择"
          allowClear
          mode="multiple"
          options={(labelList?.list ?? []).map(i => ({ value: i.id, label: i.name }))}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <>
      {filters}
      <Table
        scroll={{ y: 380 }}
        rowKey="id"
        loading={isLoading}
        showSelectedCount
        selectedCount={selectedRowKeys?.length ?? 0}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </>
  );
});

export default RuleModal;
